import { useEffect, useCallback } from "react";

/**
 * Default polling interval for real-time updates (120 seconds / 2 minutes)
 * This provides a good balance between data freshness and cost efficiency
 */
export const DEFAULT_POLLING_INTERVAL = 120000;

/**
 * Custom hook to add visibility-based refresh to RTK Query results
 *
 * This hook automatically refetches data when:
 * 1. The browser tab becomes visible again (user returns to tab)
 * 2. The window regains focus
 *
 * Usage:
 * const queryResult = useGetAppointmentsQuery({ clinicId }, { pollingInterval: 90000 });
 * useVisibilityRefresh(queryResult.refetch);
 *
 * @param {Function} refetch - The refetch function from RTK Query result
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable visibility refresh (default: true)
 * @param {boolean} options.refetchOnFocus - Whether to refetch on window focus (default: true)
 */
export const useVisibilityRefresh = (refetch, options = {}) => {
  const { enabled = true, refetchOnFocus = true } = options;

  // Safe refetch wrapper that handles skipped queries gracefully
  const safeRefetch = useCallback(() => {
    if (!refetch) return;
    try {
      refetch();
    } catch (error) {
      // Silently ignore errors from queries that haven't started
      // This happens when queries are skipped (e.g., no clinicId yet for new users)
      if (!error.message?.includes("has not been started yet")) {
        console.warn("[useVisibilityRefresh] Error during refetch:", error);
      }
    }
  }, [refetch]);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === "visible") {
      safeRefetch();
    }
  }, [safeRefetch]);

  const handleFocus = useCallback(() => {
    safeRefetch();
  }, [safeRefetch]);

  useEffect(() => {
    if (!enabled || !refetch) return;

    // Refetch when tab becomes visible
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Optionally refetch when window gains focus
    if (refetchOnFocus) {
      window.addEventListener("focus", handleFocus);
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (refetchOnFocus) {
        window.removeEventListener("focus", handleFocus);
      }
    };
  }, [enabled, refetchOnFocus, handleVisibilityChange, handleFocus]);
};

/**
 * Combines multiple refetch functions into one
 * Useful when a page has multiple queries that should all refresh together
 *
 * Usage:
 * const combinedRefetch = useCombinedRefetch([
 *   appointmentsResult.refetch,
 *   tasksResult.refetch,
 *   notificationsResult.refetch
 * ]);
 * useVisibilityRefresh(combinedRefetch);
 *
 * @param {Function[]} refetchFunctions - Array of refetch functions
 * @returns {Function} Combined refetch function
 */
export const useCombinedRefetch = (refetchFunctions) => {
  return useCallback(() => {
    refetchFunctions.forEach((refetch) => {
      if (refetch && typeof refetch === "function") {
        try {
          // Wrap in try-catch to handle skipped queries gracefully
          // RTK Query throws "Cannot refetch a query that has not been started yet"
          // when refetch is called on a query with skip: true
          refetch();
        } catch (error) {
          // Silently ignore errors from queries that haven't started
          // This happens when queries are skipped (e.g., no clinicId yet for new users)
          if (!error.message?.includes("has not been started yet")) {
            console.warn("[useRealtimeQuery] Error during refetch:", error);
          }
        }
      }
    });
  }, [refetchFunctions]);
};
